/*
 * AlfaSurvival — verwachte leverdatum (assets/alfa-levertijd.js)
 *
 * Vult elk element met [data-alfa-levertijd] ("product" | "summary" | "line") dat
 * data-lt-min / data-lt-max (werkdagen) heeft. Min/max komen uit
 * snippets/alfa-levertijd-data.liquid; de instellingen uit
 * <script type="application/json" id="alfa-levertijd-config"> in layout/theme.liquid.
 * Liquid rendert een fallback ("Levertijd 3–7 werkdagen"); dit script vervangt die
 * tekst. Faalt het script, dan blijft de fallback staan.
 *
 * Rekenregels
 *   - "Nu" is altijd Europe/Amsterdam, nooit de tijd van het toestel.
 *   - Werkdag = ma–vr en niet in lt_holidays.
 *   - Startdag = vandaag als dat een werkdag is én het uur < lt_cutoff_hour,
 *     anders de eerstvolgende werkdag.
 *   - Datum min/max = startdag + min/max werkdagen (startdag telt niet mee).
 *   - Valt de besteldag (vandaag) in een lt_delay_periods-periode, dan komen er
 *     N werkdagen bij min én max (bij overlappende periodes telt de hoogste N).
 *
 * Testgevallen (feestdagen 25/26 dec, cutoff 17, geen vertraging) —
 * draai in de console: alfaLevertijd.test()
 *   Besteld                 Niveau         Verwacht
 *   do 15 okt 2026, 10:00   snel 2–4       ma 19 – wo 21 okt
 *   do 15 okt 2026, 10:00   choice 3–7     di 20 – ma 26 okt
 *   do 15 okt 2026, 10:00   standaard 5–7  do 22 – ma 26 okt
 *   do 22 okt 2026, 10:00   standaard 5–7  do 29 okt – ma 2 nov
 *   do 15 okt 2026, 18:00   choice 3–7     wo 21 – di 27 okt   (start vr 16)
 *   za 17 okt 2026          choice 3–7     do 22 – wo 28 okt   (start ma 19)
 *   wo 23 dec 2026, 10:00   snel 2–4       ma 28 – wo 30 dec   (25/26 dec overgeslagen)
 *
 * Cart-attribuut: de samenvatting in drawer/winkelwagen wordt (altijd in NL-notatie)
 * als "Verwachte levering" via /cart/update.js weggeschreven — alleen bij een
 * gewijzigde waarde, gedebounced, zonder sections/events (dus geen rerender).
 */
(function () {
  'use strict';

  var TZ = 'Europe/Amsterdam';
  var ATTR_NAME = 'Verwachte levering';
  var DAY = 864e5;
  var YMD = /^\d{4}-\d{2}-\d{2}$/;
  var cfg = null;

  function parseHolidays(text) {
    var set = {};
    String(text || '').split(/[\r\n,;]+/).forEach(function (line) {
      line = line.trim();
      if (YMD.test(line)) set[line] = true;
    });
    return set;
  }

  function parseDelays(text) {
    return String(text || '').split(/[\r\n]+/).map(function (line) {
      var p = line.split('|').map(function (s) { return s.trim(); });
      var n = parseInt(p[2], 10);
      if (p.length !== 3 || !YMD.test(p[0]) || !YMD.test(p[1]) || isNaN(n) || n < 0) return null;
      return { from: p[0], to: p[1], n: n };
    }).filter(Boolean);
  }

  function makeConfig(raw) {
    var cutoff = parseInt(raw.cutoff, 10);
    return {
      locale: raw.locale || 'nl',
      cutoff: isNaN(cutoff) ? 17 : cutoff,
      holidays: parseHolidays(raw.holidays),
      delays: parseDelays(raw.delays),
      labels: raw.labels || {}
    };
  }

  function readConfig() {
    var el = document.getElementById('alfa-levertijd-config');
    if (!el) return null;
    try { return makeConfig(JSON.parse(el.textContent)); } catch (e) { return null; }
  }

  // ── Datumrekenen (datums als UTC-middag, zodat er nooit een dag verschuift) ──
  function amsterdamNow(now) {
    var o = {};
    new Intl.DateTimeFormat('en-CA', {
      timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23'
    }).formatToParts(now).forEach(function (p) { o[p.type] = p.value; });
    return { ymd: o.year + '-' + o.month + '-' + o.day, hour: parseInt(o.hour, 10) % 24 };
  }

  function toDate(ymd) {
    var p = ymd.split('-');
    return new Date(Date.UTC(+p[0], +p[1] - 1, +p[2], 12));
  }

  function toYmd(d) { return d.toISOString().slice(0, 10); }

  function isWorkday(d, c) {
    var w = d.getUTCDay();
    return w !== 0 && w !== 6 && !c.holidays[toYmd(d)];
  }

  function nextWorkday(d, c) {
    do { d = new Date(d.getTime() + DAY); } while (!isWorkday(d, c));
    return d;
  }

  function addWorkdays(d, n, c) {
    for (var i = 0; i < n; i++) d = nextWorkday(d, c);
    return d;
  }

  function computeRange(min, max, now, c) {
    var n = amsterdamNow(now || new Date());
    var today = toDate(n.ymd);
    var start = isWorkday(today, c) && n.hour < c.cutoff ? today : nextWorkday(today, c);
    var extra = 0;
    c.delays.forEach(function (p) {
      if (n.ymd >= p.from && n.ymd <= p.to) extra = Math.max(extra, p.n);
    });
    return { min: addWorkdays(start, min + extra, c), max: addWorkdays(start, max + extra, c) };
  }

  // ── Notatie: "di 20 – ma 26 okt", "di 27 okt – wo 4 nov", "di 20 okt" ──
  function parts(d, locale) {
    var o = {};
    new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short', timeZone: TZ })
      .formatToParts(d).forEach(function (p) { o[p.type] = p.value.replace(/\./g, '').trim(); });
    return o;
  }

  function formatRange(a, b, locale) {
    var A = parts(a, locale);
    var B = parts(b, locale);
    function one(p, withMonth) { return p.weekday + ' ' + p.day + (withMonth ? ' ' + p.month : ''); }
    if (toYmd(a) === toYmd(b)) return one(A, true);
    var sameMonth = a.getUTCMonth() === b.getUTCMonth() && a.getUTCFullYear() === b.getUTCFullYear();
    return one(A, !sameMonth) + ' – ' + one(B, true);
  }

  // ── Cart-attribuut ──
  var lastSent = null;
  var attrTimer = null;

  function sendAttr(value) {
    if (value === lastSent) return;
    lastSent = value;
    var attrs = {};
    attrs[ATTR_NAME] = value;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    try {
      fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      }).then(function (r) { if (!r.ok) lastSent = null; }).catch(function () { lastSent = null; });
    } catch (e) { lastSent = null; }
  }

  function queueAttr(el, range) {
    var value = formatRange(range.min, range.max, 'nl-NL');
    if (value === el.getAttribute('data-lt-cart-attr') || value === lastSent) return;
    clearTimeout(attrTimer);
    attrTimer = setTimeout(function () { sendAttr(value); }, 800);
  }

  // ── DOM ──
  function fill(el, now) {
    var min = parseInt(el.getAttribute('data-lt-min'), 10);
    var max = parseInt(el.getAttribute('data-lt-max'), 10);
    if (isNaN(min) || isNaN(max) || min < 0 || max < min) return;

    var type = el.getAttribute('data-alfa-levertijd');
    var range = computeRange(min, max, now, cfg);
    var text = formatRange(range.min, range.max, cfg.locale);
    var isLine = type === 'line';
    var label = (isLine ? cfg.labels.line : cfg.labels.expected) || '';
    var key = label + '|' + text;

    if (el.getAttribute('data-lt-done') !== key) {
      var target = el.querySelector('[data-lt-text]') || el;
      var strong = document.createElement(isLine ? 'span' : 'strong');
      strong.textContent = text;
      target.textContent = label ? label + (isLine ? ' ' : ': ') : '';
      target.appendChild(strong);
      el.setAttribute('data-lt-done', key);
    }

    if (type === 'summary' && el.hasAttribute('data-lt-cart-attr')) queueAttr(el, range);
  }

  function init(now) {
    if (!cfg) cfg = readConfig();
    if (!cfg) return;
    var els = document.querySelectorAll('[data-alfa-levertijd]');
    for (var i = 0; i < els.length; i++) {
      try { fill(els[i], now); } catch (e) { /* fallback-tekst blijft staan */ }
    }
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    (window.requestAnimationFrame || setTimeout)(function () { scheduled = false; init(); });
  }

  function observe() {
    if (!window.MutationObserver) return;
    var targets = [document.getElementById('CartDrawer')];
    // Winkelwagenpagina: refresh() vervangt het scope-element zelf → observeer de ouder.
    var scopes = document.querySelectorAll('[data-alfa-levertijd-scope]');
    for (var i = 0; i < scopes.length; i++) targets.push(scopes[i].parentElement);
    var mo = new MutationObserver(schedule);
    targets.forEach(function (t) { if (t) mo.observe(t, { childList: true, subtree: true }); });
  }

  // ── Test: alfaLevertijd.test() ──
  function test() {
    var c = makeConfig({ cutoff: 17, holidays: '2026-12-25\n2026-12-26', delays: '' });
    var cases = [
      ['2026-10-15T08:00:00Z', 2, 4, 'ma 19 – wo 21 okt'],
      ['2026-10-15T08:00:00Z', 3, 7, 'di 20 – ma 26 okt'],
      ['2026-10-15T08:00:00Z', 5, 7, 'do 22 – ma 26 okt'],
      ['2026-10-22T08:00:00Z', 5, 7, 'do 29 okt – ma 2 nov'],
      ['2026-10-15T16:00:00Z', 3, 7, 'wo 21 – di 27 okt'],
      ['2026-10-17T10:00:00Z', 3, 7, 'do 22 – wo 28 okt'],
      ['2026-12-23T09:00:00Z', 2, 4, 'ma 28 – wo 30 dec']
    ];
    var rows = cases.map(function (t) {
      var r = computeRange(t[1], t[2], new Date(t[0]), c);
      var got = formatRange(r.min, r.max, 'nl-NL');
      return { besteld: t[0], dagen: t[1] + '–' + t[2], verwacht: t[3], uitkomst: got, ok: got === t[3] };
    });
    if (window.console && console.table) console.table(rows);
    return rows;
  }

  window.alfaLevertijd = {
    init: init,
    test: test,
    format: function (min, max, now) {
      if (!cfg) cfg = readConfig();
      if (!cfg) return '';
      var r = computeRange(min, max, now ? new Date(now) : new Date(), cfg);
      return formatRange(r.min, r.max, cfg.locale);
    }
  };

  function boot() {
    init();
    observe();
    document.addEventListener('cart:updated', schedule);
    document.addEventListener('cart:refresh', function () { setTimeout(schedule, 300); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
